/* Talking to a broker straight from the device.
 *
 * §1 rules out broker credentials and the exception is argued in `brokers.ts`,
 * not bent quietly here: every key that reaches this file is read-only. What
 * this file is responsible for is narrower — getting the request out of the
 * page, and making sure it can only go where it was meant to.
 *
 * It only works where the shell routes requests natively. A browser enforces
 * cross-origin rules that neither broker makes any allowance for, so the call
 * simply cannot be made from a tab. That is stated on screen rather than left
 * as a failure nobody can interpret.
 *
 * Both native shells route it, by the same trick and neither by an import.
 * Android sets `CapacitorHttp` in `capacitor.config.ts`; macOS makes the
 * request in Rust and hands it back through the global Tauri already injects.
 * Configuration on one side, a host-locked command on the other — `src/`
 * mentions neither package and §2's four dependencies stand.
 *
 * What matters most: credentials go from the device to the broker and nowhere
 * else. They never reach our Worker, they are not in the JSON export, and they
 * are not in the sync blob — settings are outside both. */

import { BROKER_HOSTS } from './brokers';

type Invoke = (cmd: string, args?: Record<string, unknown>) => Promise<unknown>;

/** The desktop shell's bridge, when this is running inside it. Read off the
    global rather than imported, for the same reason the Capacitor check below
    is: an import would be a runtime dependency (§2). */
function desktopInvoke(): Invoke | null {
  const bridge = (globalThis as { __TAURI__?: { core?: { invoke?: Invoke } } }).__TAURI__;
  return typeof bridge?.core?.invoke === 'function' ? bridge.core.invoke : null;
}

function onAndroid(): boolean {
  const cap = (globalThis as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
  return cap?.isNativePlatform?.() === true;
}

/** Whether this shell can reach a broker directly. */
export function canReachBrokers(): boolean {
  return onAndroid() || desktopInvoke() !== null;
}

export class BrokerLinkUnavailable extends Error {
  constructor() {
    super('A browser tab cannot reach a broker directly. Use the installed app, or import a file.');
    this.name = 'BrokerLinkUnavailable';
  }
}

/* The check that has to be right, and the reason it is here as well as in Rust.
 *
 * The desktop command refuses anything but the listed hosts, so this is not
 * what stops a bad URL — that is the point of putting it there. This stops one
 * being built at all: a signed request carries a credential in its headers, and
 * a URL assembled wrong would hand it to whoever answers. Cheaper to refuse it
 * before it is signed than to rely on the far end saying no. */
export function allowedHost(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' && BROKER_HOSTS.includes(parsed.hostname);
  } catch {
    return false;
  }
}

/* Whatever was thrown, said in words.
 *
 * Errors arrive here in three shapes — an Error from `fetch`, a bare string
 * from the desktop bridge, and occasionally an object with a message on it —
 * and only the first survives an `instanceof` check. */
export function describe(e: unknown): string {
  if (typeof e === 'string' && e.trim() !== '') return e;
  if (e instanceof Error && e.message !== '') return e.message;
  if (e && typeof e === 'object' && 'message' in e) {
    const m = (e as { message?: unknown }).message;
    if (typeof m === 'string' && m.trim() !== '') return m;
  }
  return 'no reason given';
}

export interface BrokerRequest {
  url: string;
  /** Sent as given. MEXC signs a request in its headers rather than its query,
      so this is not optional decoration. */
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

/** One GET, returned as text. The caller decides whether that text is XML or
    JSON — this layer has no opinion and no parser. */
export async function brokerGet({ url, headers, signal }: BrokerRequest): Promise<string> {
  if (!canReachBrokers()) throw new BrokerLinkUnavailable();
  if (!allowedHost(url)) throw new Error('That is not a broker this app connects to.');

  const invoke = desktopInvoke();
  if (invoke) {
    /* The desktop shell fetches this in Rust, where the page's cross-origin
       rules and its CSP do not apply. There is no way to hand an abort signal
       across that boundary, so the only honest thing is to stop before it
       starts and to drop the answer if it arrives after the caller gave up. */
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    let body: unknown;
    try {
      body = await invoke('broker_get', { url, headers: headers ?? {} });
    } catch (e) {
      /* The command is `Result<String, String>` on the Rust side, so a refusal
         crosses the bridge as a plain string and not as an Error. Rethrown as
         it arrived, every caller's `e instanceof Error` is false and the screen
         says "unknown error" — throwing away the one thing the failure was
         carrying, which is why it failed. It is wrapped here, once, rather than
         at each of the places that report it. */
      throw new Error(describe(e));
    }
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    return String(body);
  }

  const res = await fetch(url, { signal, headers: { accept: 'application/json', ...headers } });
  if (!res.ok) {
    /* The body says why, where a status alone does not: MEXC's spot API refuses
       with 400 and puts the reason — a bad signature, a missing permission — in
       the JSON. Kept short, since it goes on screen. */
    const said = await res.text().catch(() => '');
    throw new Error(`The broker answered ${res.status}.${said ? ` ${said.slice(0, 200)}` : ''}`);
  }
  return res.text();
}

const FLEX = 'https://ndcdyn.interactivebrokers.com/AccountManagement/FlexWebService';

/** IBKR hands out a statement in two steps: ask, then collect. Both carry the
    token, and neither can do anything but read. */
export async function fetchFlexStatement(
  token: string,
  queryId: string,
  signal?: AbortSignal,
): Promise<string> {
  const asked = await brokerGet({
    url: `${FLEX}/SendRequest?t=${encodeURIComponent(token)}&q=${encodeURIComponent(queryId)}&v=3`,
    headers: { accept: 'application/xml' },
    signal,
  });

  const reference = /<ReferenceCode>([^<]+)<\/ReferenceCode>/.exec(asked)?.[1];
  if (!reference) {
    const why = /<ErrorMessage>([^<]*)<\/ErrorMessage>/.exec(asked)?.[1];
    throw new Error(why ?? 'The broker did not return a statement reference.');
  }

  /* The report is generated on their side; asking for it immediately can arrive
     before it exists. A few tries, spaced out, rather than one and a shrug. */
  for (let attempt = 0; attempt < 5; attempt++) {
    const body = await brokerGet({
      url: `${FLEX}/GetStatement?q=${encodeURIComponent(reference)}&t=${encodeURIComponent(token)}&v=3`,
      headers: { accept: 'application/xml' },
      signal,
    });
    if (body.includes('<FlexQueryResponse')) return body;
    if (!/statement generation in progress/i.test(body)) return body;
    await new Promise((r) => setTimeout(r, 1500));
  }
  throw new Error('The statement was still being generated after several tries.');
}
